import React, { useState, useEffect } from 'react';
import { useReview } from '../../context/ReviewContext';
import { reviewModel, reviewUtils } from '../../data/reviewModel';

const ReviewForm = ({ 
  booking, 
  revieweeId, 
  revieweeName, 
  revieweeRole, 
  onClose, 
  onSuccess 
}) => {
  const { createReview, reviews } = useReview();
  const [formData, setFormData] = useState({
    rating: 5,
    title: '',
    comment: '',
    tags: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [existingReview, setExistingReview] = useState(null);

  const availableTags = revieweeRole === 'girl' 
    ? reviewModel.tags.GIRL_TAGS 
    : reviewModel.tags.CUSTOMER_TAGS;

  // Check if there's an existing review for this booking
  useEffect(() => {
    if (booking && reviews) {
      const existing = reviews.find(review => 
        review.bookingId === booking.id && 
        review.reviewerId === booking.userId
      );
      
      if (existing) {
        setExistingReview(existing);
        setIsEditMode(true);
        setFormData({
          rating: existing.rating,
          title: existing.title,
          comment: existing.comment,
          tags: existing.tags || []
        });
      }
    }
  }, [booking, reviews]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTagToggle = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag].slice(0, 5) // Limit to 5 tags
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.comment.trim()) {
      setError('กรุณากรอกหัวข้อและความคิดเห็น');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const reviewData = {
        bookingId: booking.id,
        revieweeId,
        revieweeName,
        revieweeRole,
        reviewedAt: booking.bookingDate || new Date().toISOString(),
        ...formData
      };

      console.log('ReviewForm: Submitting review data:', reviewData);
      console.log('ReviewForm: Booking ID:', booking.id);
      console.log('ReviewForm: Reviewee ID:', revieweeId);
      console.log('ReviewForm: Reviewee Name:', revieweeName);
      console.log('ReviewForm: Reviewee Role:', revieweeRole);
      
      const reviewId = await createReview(reviewData);
      console.log('ReviewForm: Created review with ID:', reviewId);
      
      if (onSuccess) {
        onSuccess();
      }
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error creating review:', error);
      setError(error.message || 'เกิดข้อผิดพลาดในการสร้างรีวิว');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTagLabel = (tag) => {
    const tagLabels = {
      // Girl tags
      friendly: 'เป็นมิตร',
      professional: 'เป็นมืออาชีพ',
      punctual: 'ตรงเวลา',
      attractive: 'สวยงาม',
      good_service: 'บริการดี',
      clean: 'สะอาด',
      fun: 'สนุก',
      caring: 'ใส่ใจ',
      communicative: 'สื่อสารดี',
      reliable: 'น่าเชื่อถือ',
      
      // Customer tags
      respectful: 'ให้เกียรติ',
      polite: 'สุภาพ',
      generous: 'ใจกว้าง',
      understanding: 'เข้าใจ',
      appreciative: 'รู้คุณค่า'
    };
    
    return tagLabels[tag] || tag;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {isEditMode ? 'แก้ไขรีวิว' : 'รีวิว'} {revieweeName}
        </h2>
        <p className="text-gray-600">
          แชร์ประสบการณ์ของคุณกับ {revieweeRole === 'girl' ? 'น้อง' : 'ลูกค้า'}คนนี้
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            ให้คะแนน (1-5 ดาว)
          </label>
          <div className="flex space-x-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                className={`text-3xl transition-colors ${
                  star <= formData.rating ? 'text-yellow-500' : 'text-gray-300'
                } hover:text-yellow-500`}
              >
                ★
              </button>
            ))}
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {reviewUtils.getRatingText(formData.rating)}
          </p>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            หัวข้อรีวิว *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="สรุปประสบการณ์ของคุณในประโยคสั้นๆ"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            maxLength={100}
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.title.length}/100 ตัวอักษร
          </p>
        </div>

        {/* Comment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            รายละเอียดรีวิว *
          </label>
          <textarea
            name="comment"
            value={formData.comment}
            onChange={handleInputChange}
            placeholder="เล่าประสบการณ์ของคุณอย่างละเอียด..."
            rows={4}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            maxLength={1000}
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.comment.length}/1000 ตัวอักษร
          </p>
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            แท็ก (เลือกได้สูงสุด 5 ตัว)
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {availableTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => handleTagToggle(tag)}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                  formData.tags.includes(tag)
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                }`}
                disabled={!formData.tags.includes(tag) && formData.tags.length >= 5}
              >
                {getTagLabel(tag)}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            เลือกแล้ว {formData.tags.length}/5 ตัว
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Submit Buttons */}
        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 bg-blue-500 text-white py-3 px-4 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'กำลังส่ง...' : (isEditMode ? 'อัปเดตรีวิว' : 'ส่งรีวิว')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            ยกเลิก
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReviewForm;
